import { MetaDataDto, NodeTypes } from 'dto/MetadataDto';
import { NodeConnectionUi } from 'classes/NodeConnectionUi';
import { FlowResultDto } from 'dto/FlowResultDto';
import { Observable } from 'rxjs/Rx';
import { NodeConnection, NodeConnectionNames } from 'dto/NodeConnection';
import { ApiService } from 'services/api.service';
import { FlowConfig } from 'dto/FlowConfig';
import { EventEmitter, Output } from '@angular/core';
import { FlowService } from 'services/flow.service';
import { BaseNodeConfig } from 'dto/NodeConfig';
import { NodeUi } from 'classes/NodeUi';

export class FlowUi {
    constructor(private api: ApiService, public dto: FlowConfig) {
    }
    
    SubFlowOutputs = {};
    Nodes: NodeUi[] = [];
    Connections: NodeConnectionUi[] = [];
    RootNode: NodeUi;
    NodeHeight: number = 80;
    NodeWidth: number = 180;
    FlowResult: FlowResultDto;
    flowSaved: EventEmitter<any> = new EventEmitter();
    
    /** Build the tree structure
     *         ____
     *        [node]
     *       /      \
     *    [node]   [node]
     * 
     * Also shifts all the connection points and the attached nodes to the end of a node-group
    */
    public Compile() {
        //initiate/remap
        for (let node of this.dto.Nodes) {
            let nodeUi = this.Nodes.find(nodeUi => nodeUi.dto.Identifier == node.Identifier);
            if (nodeUi)
                nodeUi.Reset(node);
            else
            {
                nodeUi = new NodeUi(this.api, node, this);
                this.Nodes.push(nodeUi);
            }
            nodeUi.UiHeight = this.NodeHeight;
            nodeUi.UiWidth = this.NodeWidth;
        }
        // Iterate backwards over the list of nodeUis to avoid messing up our indices too much upon removal.
        for (let i = this.Nodes.length - 1; i >= 0; i--)
        {
            if (!this.dto.Nodes.some(node => node.Identifier == this.Nodes[i].dto.Identifier))
                this.Nodes.splice(i,1);
        }

        // build the tree
        for (let node of this.Nodes) {

            //check for each node to what expression/node it is tied and apply these as parent nodes
            for (let cursorNode of this.Nodes) {
                if (cursorNode.dto.TriggeredBy.find(trigger => trigger.Identifier == node.dto.Identifier)) {
                    node.Childs.push(cursorNode);
                }
            }

            //drill down to make child nodes easily accessable by a child-parent structure.
            for (var child of node.Childs) {
                child.Parents.push(node);
            }

        }

        //find root node (=node without parents)
        this.RootNode = this.Nodes.find(node => node.dto.TriggeredBy.length == 0);

        this.RootNode.ShiftChildren();

        for (let nodeUi of this.Nodes) {
            let endOfThisGroup = nodeUi.GetEndOfThisGroup();
            // First add all existing connections.
            // NOTE: Can this be optimised? It's inefficient now, since it searches the entire tree O(n²)
            // We've already done that above, but lost that information when we ShiftChildren().
            // However ShiftChildren generates information that is necessary to correctly add the connections.
            this.Nodes.filter(node => node.dto.TriggeredBy.some(trigger => trigger.Identifier == nodeUi.dto.Identifier)).forEach(child => {
                child.dto.TriggeredBy.filter(trigger => trigger.Identifier == nodeUi.dto.Identifier).forEach(trigger => 
                {
                    if (trigger.Name == NodeConnectionNames.ALWAYS_TRIGGERED)
                        nodeUi.Connections.push(this.getOrCreateNodeConnection(nodeUi, trigger,nodeUi));
                    else
                        endOfThisGroup.Connections.push(this.getOrCreateNodeConnection(nodeUi, trigger, endOfThisGroup));
                });
            });

            // Every node has to have an always continue node! 
            if (!nodeUi.Connections.some(conn => conn.Dto.Name == NodeConnectionNames.ALWAYS_TRIGGERED))
                nodeUi.Connections.push(this.getOrCreateNodeConnection(nodeUi, new NodeConnection(nodeUi.dto.Identifier, NodeConnectionNames.ALWAYS_TRIGGERED, null),nodeUi));

            // Find all the connection points based on the template or the metadata of the uielement
            let connections = [];
            if (nodeUi.dto.TemplateId) {
                nodeUi.Template = this.api.getExpressionTemplate(nodeUi.dto.TemplateId);
                if (nodeUi.Template && nodeUi.Template.Outputs)
                    connections.push(...nodeUi.Template.Outputs);
            }

            if (nodeUi.dto.NodeType == NodeTypes.SubflowNode)
                connections.push(...(this.SubFlowOutputs[nodeUi.dto.Identifier]));

            connections.forEach(connectionDto => {
                // Add this connection from the metadata only if no identical connection has been added.
                if (!endOfThisGroup.Connections.some(conn => conn.From === nodeUi && conn.Dto.Name == connectionDto.Name && conn.Dto.Value == connectionDto.Value))
                {
                    let clone = Object.assign({}, connectionDto); //clone this object to get rid of the instance. (no deep-copy)
                    endOfThisGroup.Connections.push(this.getOrCreateNodeConnection(nodeUi, clone, endOfThisGroup));
                }
            });
        }

        // Flush the old connections list, and rebuild it from the this.Nodes Connections
        this.Connections = [];
        for (let node of this.Nodes)
            this.Connections.push(...node.Connections)
    }

    /** Try to find a matching NodeConnectionUi in this.Connections. Otherwise, create a new one.
     * 
     * This method is used to ensure that we re-use DOM objects instead of re-creating them unnecessarily.
     * It is used during Compile(), when the individual NodeUi objects in this.Nodes have been Reset(),
     * and we rebuild their Connections list. After all NodeUis have had their Connections updated, this.Connections
     * is rebuild from those connections, to ensure that we discard any unused ones.
     * 
     * @param from The NodeUi this connection originates from (not necessarily grahpically)
     * @param dto The DTO of this connection. dto.Identifier === from.dto.Identifier
     * @param drawFrom The NodeUi this connection origniates from graphically
     */
    private getOrCreateNodeConnection(from:NodeUi, dto:NodeConnection, drawFrom:NodeUi):NodeConnectionUi {
        let nodeUi = this.Connections.find(conn => conn.From === from && conn.Dto.Name == dto.Name && conn.Dto.Value == dto.Value);
        if (!nodeUi)
            nodeUi = new NodeConnectionUi(from, dto, drawFrom);
        else
            nodeUi.Reset(drawFrom);
        return nodeUi;
    }

    /**
     * Calculates all the X and Y coordinates for the nodes and connections.
     */
    public ReRender(): void {
        this.RootNode.UpdateColumnWidth(0);
        this.RootNode.UpdateY();
        this.Nodes.forEach(node => node.ReRender());
    }

    /**
     * Recompiles, redraws and then saves the flow (to the Api)
     */
    public Refresh() {
        this.Compile();
        this.ReRender();
        this.Save();
    }

    /**
     * Post the flow to the api. On successful save the 'flowSaved' event is emmitted.
     */
    public Save() {
        this.api.saveFlowConfig(this.dto).subscribe(
            result => this.flowSaved.emit(),
            error => console.error('Error: ' + error));
    }

    /**
     * Insert a new subtree, defined by its root node at the given NodeConnection
     * Existing Childs are moved to the bottom of the inserted subtree.
     * @param branchRoot The root-node of the moving branch (branch can be just a single node)
     * @param connectionPoint The point where to connect the moving node 
     */
    public insertSubtreeAtConnectionPoint(branchRoot: NodeUi, connectionPoint: NodeConnectionUi) {
        if (connectionPoint.To) // If we're not trying to insert it on a loose connection end
        {
            // Get the bottom node of the branch below the subtreeRoot. This can be the root itself.
            var branchBottomNode = branchRoot.GetSingleEndOfBranch();
            var attachmentNode = branchBottomNode;
            if (!branchBottomNode) // If the branch to insert is not a straight chain, (it has a point where it splits)
            {
                if (!connectionPoint.To.IsRootOfGroupOnly())
                    throw Error('You cannot insert a branch that splits into the tree. On what end should the nodes that have to be moved down be attached?');
                // Find the bottom node of the subtree group.
                // Use that as attachment point for the original target of the connectionPoint
                attachmentNode = branchRoot.GetEndOfThisGroup();

                // Move attachmentNode TriggeredBy for Names other than ALWAYS down to the bottom of the branch below the insertion point.
                var triggersToMove = attachmentNode.dto.TriggeredBy.filter(trigger =>
                    trigger.Name != NodeConnectionNames.ALWAYS_TRIGGERED); // Select all not ALWAYS triggered
                let connectionpointToEndOfGroup = connectionPoint.To.GetEndOfThisGroup();
                connectionpointToEndOfGroup.dto.TriggeredBy.push(...triggersToMove)
                attachmentNode.dto.TriggeredBy = attachmentNode.dto.TriggeredBy.filter(trigger =>
                    trigger.Name == NodeConnectionNames.ALWAYS_TRIGGERED);
            }
            // Move connectionpoint.To subtree to the end of the inserted branch.
            // Detach from parent by retaining only the triggers that do not match the connectionPoint.
            connectionPoint.To.dto.TriggeredBy = connectionPoint.To.dto.TriggeredBy.filter(trigger =>
                !(trigger.Identifier == connectionPoint.Dto.Identifier
                    && trigger.Name == connectionPoint.Dto.Name
                    && trigger.Value == connectionPoint.Dto.Value));
            // Reattach unlinked subtree to the base of the inserted branch.
            connectionPoint.To.dto.TriggeredBy.push(new NodeConnection(attachmentNode.dto.Identifier, NodeConnectionNames.ALWAYS_TRIGGERED, null))
        }
        //attach branchRoot to parentNode using connectionPoint
        branchRoot.dto.TriggeredBy.push(connectionPoint.Dto);
        // Add new node DTO to the flow DTO
        if (!this.Nodes.find(node => node.dto.Identifier == branchRoot.dto.Identifier))
            this.dto.Nodes.push(branchRoot.dto);
        // Re-compile the flow to ensure that the DTO changes are reflected in the Ui elements.
        this.Compile();
    }

    /**
     * Adds a new output to the current node with an empty expression node attached.
     */
    public addNewNode(trigger: NodeConnectionUi, template?: MetaDataDto): void {

        if(!template) //todo: ask what template to insert
            template = this.api.getExpressionTemplate('XPR');
        
        // Create a new NodeDto with a unique identifier.
        let newNodeDto:BaseNodeConfig = new BaseNodeConfig(template);

        // Try to insert the new node.
        let newNode = new NodeUi(this.api,newNodeDto, this);
        this.insertSubtreeAtConnectionPoint(newNode, trigger);
    }

    public MoveToConnectionPoint(branchRoot: NodeUi, connectionPoint: NodeConnectionUi) {
        if (connectionPoint.To && branchRoot.dto.Identifier == connectionPoint.To.dto.Identifier)
            // It's already in the right place.
            return;
        if (connectionPoint.From.dto.Identifier == branchRoot.dto.Identifier)
            throw Error('A node cannot be attached to its own result.')
        // In these cases we move only a single node:
        if (branchRoot.Childs.length == 1 // There is exactly one child and
            && (connectionPoint.From.GetAncestors().some(node => node.dto.Identifier == branchRoot.dto.Identifier) // Either we would create a loop by moving the branch
                || confirm('Do you to want to move only this node? Otherwise the sub-tree will be moved as well.'))) // Or the user wants to move only one node.
        {
            // Delete dropped Node from the flow.
            this.deleteNode(branchRoot);
            // Clean droppedNode of references to its old subtree
            branchRoot.Childs = [];
            branchRoot.Connections = [];
        }
        else if (connectionPoint.From.GetAncestors().some(node => node.dto.Identifier == branchRoot.dto.Identifier))
            throw Error('You cannot attach a branch to itself. It creates an infinite loop.');
        else
            // Remove this node from all parent.Childs
            branchRoot.Parents.forEach(parent => parent.Childs.splice(parent.Childs.findIndex(child => child.dto.Identifier == branchRoot.dto.Identifier)))
        // Clean droppedNode of references to its old parrents.
        branchRoot.Parents = [];
        branchRoot.dto.TriggeredBy = [];
        // Insert at new location
        this.insertSubtreeAtConnectionPoint(branchRoot, connectionPoint);
    }

    /** Delete a single node from the flow.
     * 
     * Child nodes are attached to the parent of the deleted node.
     * 
     * @param node The node that should be deleted.
     */
    public deleteNode(node: NodeUi) {
        if (this.Nodes.length == 1) {
            throw Error('You cannot delete the last node in a flow.');
        }
        if (node.Childs.some(child => child.dto.TriggeredBy.some(trigger => trigger.Identifier == node.dto.Identifier && trigger.Name != NodeConnectionNames.ALWAYS_TRIGGERED))) {
            throw Error(`This node has at least one child node attached to a result of this node, and it is unclear how these should be moved
                Please move and/or delete child nodes attached to the result of this node and then retry.`);
        }

        // Get the always connection (may be null). We needn't check for the conn.Dto.Name because if any other Name is present the error above is thrown.
        let alwaysConn = node.Connections.find(conn => conn.From === node && conn.Implemented)
        if (alwaysConn){
            // Set the TrigggeredBy of the child to only those that are not triggered by this node.
            alwaysConn.To.dto.TriggeredBy = alwaysConn.To.dto.TriggeredBy.filter(trigger => trigger.Identifier != node.dto.Identifier);
            // Now map all the transitions from parent to this node so they point directly from parent to this node's child.
            alwaysConn.To.dto.TriggeredBy.push(...node.dto.TriggeredBy);
        }
        // Remove this node from the flow.
        node.Flow.dto.Nodes.splice(node.Flow.dto.Nodes.indexOf(node.dto), 1);
        // Re-compile the flow to ensure that the DTO changes are reflected in the Ui elements.
        this.Compile();
    }

    /** Asynchronous wrapper to delete a single node from the flow
     * 
     * To be called from the user interface.
     * Child nodes are attached to the parent of the deleted node.
     * @param node The node that should be deleted
     * @param skipConfirmation If true, do not prompt the user to confirm.
     */
    public deleteNodeASync(node: NodeUi, skipConfirmation?: boolean): Observable<string> {
        return Observable.create(obs => {
            try {
                if (this.Nodes.length == 1) {
                    throw Error('You cannot remove the last node in the flow.');
                }
                if (skipConfirmation || confirm("delete this node?")) {
                    this.deleteNode(node);
                    this.ReRender();
                    this.Save();
                    obs.next('deleted');
                    obs.complete();
                }
                obs.next('cancelled');
                obs.complete();
            }
            catch (Error) {
                alert(Error);
                obs.next('error');
                obs.complete();
            }
        });
    }
}


// WEBPACK FOOTER //
// C:/git/flowdesigner/src/classes/FlowUi.ts